import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Footer } from '@/components/layout/Footer';

type ViewMode = 'login' | 'forgot' | 'reset' | 'magic' | 'two-factor';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [searchParams] = useSearchParams();

  const resetToken = searchParams.get('reset_token');
  const magicToken = searchParams.get('magic_token');

  const [view, setView] = useState<ViewMode>(resetToken ? 'reset' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Handle magic link token on mount
  useEffect(() => {
    if (magicToken) {
      verifyMagicLink(magicToken);
    }
  }, [magicToken]);

  async function verifyMagicLink(token: string) {
    setLoading(true);
    const res = await api.post('/auth/verify-magic-link', { token });
    if (res.success) {
      await refresh();
      navigate('/');
    } else {
      setError(res.error || 'Magic link is invalid or expired');
      setLoading(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const res = await login(email, password);
    if (res.success) {
      if (res.requiresTwoFactor) {
        setView('two-factor');
        setSuccessMessage('A verification code has been sent to your email.');
      } else {
        navigate('/');
      }
    } else {
      setError(res.error || 'Login failed');
    }
    setLoading(false);
  }

  async function handleTwoFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const res = await login(email, password, twoFactorCode);
    if (res.success && !res.requiresTwoFactor) {
      navigate('/');
    } else {
      setError(res.error || 'Invalid verification code');
    }
    setLoading(false);
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    await api.post('/auth/forgot-password', { email });
    setSuccessMessage('If that email exists, a password reset link has been sent.');
    setLoading(false);
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    const res = await api.post('/auth/reset-password', { token: resetToken, newPassword });
    if (res.success) {
      setSuccessMessage('Password has been reset. You can now sign in.');
      setView('login');
    } else {
      setError(res.error || 'Failed to reset password');
    }
    setLoading(false);
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    await api.post('/auth/magic-link', { email });
    setSuccessMessage('If that email exists, a magic sign-in link has been sent.');
    setLoading(false);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-alt px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/captive_path_logo.webp" alt="Captive Path" className="mx-auto mb-4 h-10" />
          <p className="mt-1 text-sm text-text-muted">
            {view === 'login' && 'Sign in to your account'}
            {view === 'forgot' && 'Reset your password'}
            {view === 'reset' && 'Choose a new password'}
            {view === 'magic' && 'Sign in with magic link'}
            {view === 'two-factor' && 'Enter verification code'}
          </p>
        </div>

        {successMessage && (
          <div className="mb-4 rounded-md bg-verdict-green-bg p-3 text-xs text-verdict-green">
            {successMessage}
          </div>
        )}

        {magicToken && loading && (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface p-8 shadow-card">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-teal border-t-transparent" />
            <p className="text-sm text-text-muted">Verifying magic link...</p>
          </div>
        )}

        {/* Login Form */}
        {view === 'login' && !magicToken && (
          <form onSubmit={handleLogin} className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5 shadow-card">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error && <p className="rounded-md bg-error-bg p-2 text-xs text-error">{error}</p>}
            <Button type="submit" loading={loading}>Sign In</Button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => { setView('forgot'); setError(''); setSuccessMessage(''); }}
                className="text-xs text-text-muted hover:text-teal transition-colors"
              >
                Reset Your Password
              </button>
              <button
                type="button"
                onClick={() => { setView('magic'); setError(''); setSuccessMessage(''); }}
                className="text-xs text-text-muted hover:text-teal transition-colors"
              >
                Email Magic Link
              </button>
            </div>
          </form>
        )}

        {/* Forgot Password Form */}
        {view === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5 shadow-card">
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
            {error && <p className="rounded-md bg-error-bg p-2 text-xs text-error">{error}</p>}
            <Button type="submit" loading={loading}>Send Reset Link</Button>
            <button
              type="button"
              onClick={() => { setView('login'); setError(''); setSuccessMessage(''); }}
              className="text-xs text-text-muted hover:text-teal transition-colors text-center"
            >
              Back to sign in
            </button>
          </form>
        )}

        {/* Reset Password Form (from email link) */}
        {view === 'reset' && (
          <form onSubmit={handleResetPassword} className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5 shadow-card">
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            {error && <p className="rounded-md bg-error-bg p-2 text-xs text-error">{error}</p>}
            <Button type="submit" loading={loading}>Reset Password</Button>
            <button
              type="button"
              onClick={() => { setView('login'); setError(''); setSuccessMessage(''); }}
              className="text-xs text-text-muted hover:text-teal transition-colors text-center"
            >
              Back to sign in
            </button>
          </form>
        )}

        {/* Magic Link Form */}
        {view === 'magic' && (
          <form onSubmit={handleMagicLink} className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5 shadow-card">
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
            {error && <p className="rounded-md bg-error-bg p-2 text-xs text-error">{error}</p>}
            <Button type="submit" loading={loading}>Send Magic Link</Button>
            <button
              type="button"
              onClick={() => { setView('login'); setError(''); setSuccessMessage(''); }}
              className="text-xs text-text-muted hover:text-teal transition-colors text-center"
            >
              Back to sign in
            </button>
          </form>
        )}

        {/* Two-Factor Verification Form */}
        {view === 'two-factor' && (
          <form onSubmit={handleTwoFactorSubmit} className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5 shadow-card">
            <Input
              label="Verification Code"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              placeholder="Enter the 6-digit code"
              required
            />
            {error && <p className="rounded-md bg-error-bg p-2 text-xs text-error">{error}</p>}
            <Button type="submit" loading={loading}>Verify</Button>
            <button
              type="button"
              onClick={() => { setView('login'); setError(''); setSuccessMessage(''); setTwoFactorCode(''); }}
              className="text-xs text-text-muted hover:text-teal transition-colors text-center"
            >
              Back to sign in
            </button>
          </form>
        )}
        <Footer />
      </div>
    </div>
  );
}
